'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import Badge from '@/components/shared/Badge';
import RemovedContent from '@/components/shared/RemovedContent';
import ReportModal from '@/components/shared/ReportModal';
import ShareModal from '@/components/shared/ShareModal';
import { mockListings } from '@/data/mockMarketplace';
import { ArrowLeft, ShoppingBag, Archive, Car, Bike, Search } from 'lucide-react';

function AvatarInitials({ name }: { name: string }) {
  const initials = name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div
      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-display font-bold text-[12px]"
      style={{ background: 'linear-gradient(135deg, rgba(76,201,255,0.20), rgba(122,92,255,0.20))', border: '1px solid rgba(76,201,255,0.20)', color: '#4CC9FF' }}
    >
      {initials}
    </div>
  );
}

function getStatusBadge(status: string) {
  if (status === 'Available') return 'available' as const;
  if (status === 'Pending') return 'pendingSale' as const;
  return 'sold' as const;
}

function getCategoryIcon(category: string) {
  const style = { color: 'rgba(167,163,196,0.15)', margin: '0 auto 10px' };
  if (category === 'Storage') return <Archive size={48} style={style} />;
  if (category === 'Parking') return <Car size={48} style={style} />;
  if (category === 'Bicycles') return <Bike size={48} style={style} />;
  if (category === 'Wanted') return <Search size={48} style={style} />;
  return <ShoppingBag size={48} style={style} />;
}

function getCategoryColor(category: string): string {
  const map: Record<string, string> = { Storage: '#F0A832', Parking: '#2DD4A0', Bicycles: '#4CC9FF', Wanted: '#9B7FFF' };
  return map[category] || '#4CC9FF';
}

interface ListingDetailPageProps {
  params: { listingSlug: string };
}

const REMOVED_SLUGS = ['removed-listing'];

export default function ListingDetailPage({ params }: ListingDetailPageProps) {
  const [reportOpen, setReportOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  if (REMOVED_SLUGS.includes(params.listingSlug)) {
    return (
      <div className="min-h-screen" style={{ background: '#07060C' }}>
        <SiteHeader theme="dark" />
        <main className="pt-16">
          <div className="max-w-2xl mx-auto px-6 py-20">
            <RemovedContent variant="listing" backHref="/marketplace" backLabel="Back to Marketplace" />
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const listing = mockListings.find((l) => l.slug === params.listingSlug);

  if (!listing) {
    return (
      <div className="min-h-screen" style={{ background: '#07060C' }}>
        <SiteHeader theme="dark" />
        <main className="pt-16">
          <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="max-w-screen-lg mx-auto px-6 lg:px-10 py-4">
              <Link href="/marketplace" className="inline-flex items-center gap-2 text-[13px] transition-colors duration-150 hover:opacity-80" style={{ color: '#A7A3C4' }}>
                <ArrowLeft size={14} /> Back to Marketplace
              </Link>
            </div>
          </div>
          <div className="max-w-2xl mx-auto px-6 py-20">
            <RemovedContent variant="listing" title="Listing Not Found" description="This listing doesn't exist or may have been removed." backHref="/marketplace" backLabel="Back to Marketplace" />
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const relatedListings = mockListings.filter((l) => l.id !== listing.id && l.category === listing.category).slice(0, 3);
  const isNewCategory = ['Storage', 'Parking', 'Bicycles', 'Wanted'].includes(listing.category);
  const categoryColor = getCategoryColor(listing.category);
  const isWanted = listing.category === 'Wanted';
  const isMonthly = listing.priceDisplay.includes('/mo');

  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="dark" />
      <main className="pt-16">
        {/* Back nav */}
        <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-screen-lg mx-auto px-6 lg:px-10 py-4">
            <Link href="/marketplace" className="inline-flex items-center gap-2 text-[13px] transition-colors duration-150 hover:opacity-80" style={{ color: '#A7A3C4' }}>
              <ArrowLeft size={14} /> Back to Marketplace
            </Link>
          </div>
        </div>

        <div className="max-w-screen-lg mx-auto px-6 lg:px-10 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Image gallery */}
            <div>
              <div
                className="rounded-xl flex items-center justify-center aspect-square mb-3"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                <div className="text-center">
                  {getCategoryIcon(listing.category)}
                  <p className="text-[13px]" style={{ color: 'rgba(167,163,196,0.30)' }}>{listing.category}</p>
                </div>
              </div>
              <div className="flex gap-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="w-16 h-16 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <ShoppingBag size={16} style={{ color: 'rgba(167,163,196,0.15)' }} />
                  </div>
                ))}
              </div>
            </div>

            {/* Listing details */}
            <div className="space-y-5">
              <div>
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span
                    className="text-[11px] px-2.5 py-1 rounded font-medium"
                    style={{
                      background: isNewCategory ? `${categoryColor}10` : 'rgba(76,201,255,0.08)',
                      color: isNewCategory ? categoryColor : '#4CC9FF',
                      border: `1px solid ${isNewCategory ? `${categoryColor}20` : 'rgba(76,201,255,0.15)'}`,
                    }}
                  >
                    {listing.category}
                  </span>
                  {isWanted && (
                    <span className="text-[11px] px-2.5 py-1 rounded font-medium" style={{ background: 'rgba(155,127,255,0.10)', color: '#9B7FFF', border: '1px solid rgba(155,127,255,0.20)' }}>
                      Wanted Post
                    </span>
                  )}
                  {isMonthly && (
                    <span className="text-[11px] px-2.5 py-1 rounded font-medium" style={{ background: 'rgba(45,212,160,0.10)', color: '#2DD4A0', border: '1px solid rgba(45,212,160,0.20)' }}>
                      Monthly
                    </span>
                  )}
                  <Badge variant={getStatusBadge(listing.status)} label={listing.status} size="md" />
                </div>
                <h1 className="font-display font-bold text-[24px] sm:text-[28px] leading-tight mb-3" style={{ color: '#F2F4FF' }}>
                  {listing.title}
                </h1>
                <div className="font-display font-bold text-[32px]" style={{ color: '#F2F4FF' }}>
                  {listing.priceDisplay}
                </div>
                {listing.condition && listing.condition !== 'N/A' && (
                  <p className="text-[13px] mt-1" style={{ color: '#A7A3C4' }}>
                    Condition: <span style={{ color: 'rgba(242,244,255,0.75)' }}>{listing.condition}</span>
                  </p>
                )}
              </div>

              {listing.status === 'Sold' && (
                <div className="px-4 py-3 rounded-lg" style={{ background: 'rgba(167,163,196,0.06)', border: '1px solid rgba(167,163,196,0.15)' }}>
                  <p className="text-[13px] font-medium" style={{ color: '#A7A3C4' }}>This item has been sold and is no longer available.</p>
                </div>
              )}
              {listing.status === 'Pending' && (
                <div className="px-4 py-3 rounded-lg" style={{ background: 'rgba(185,123,26,0.06)', border: '1px solid rgba(185,123,26,0.15)' }}>
                  <p className="text-[13px] font-medium" style={{ color: '#F0A832' }}>A sale is pending on this item.</p>
                </div>
              )}

              <p className="text-[14px] leading-relaxed" style={{ color: 'rgba(242,244,255,0.75)' }}>
                {listing.description}
              </p>

              {listing.notes && (
                <div className="px-4 py-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <p className="text-[12px] font-medium uppercase tracking-wider mb-1" style={{ color: 'rgba(167,163,196,0.50)' }}>Notes</p>
                  <p className="text-[13.5px]" style={{ color: 'rgba(242,244,255,0.70)' }}>{listing.notes}</p>
                </div>
              )}

              {/* Seller */}
              <div className="neon-card p-4">
                <p className="text-[12px] font-medium uppercase tracking-wider mb-3" style={{ color: 'rgba(167,163,196,0.50)' }}>Listed by</p>
                <div className="flex items-center gap-3">
                  <AvatarInitials name={listing.sellerName} />
                  <div>
                    <p className="font-semibold text-[14px]" style={{ color: '#F2F4FF' }}>{listing.sellerName}</p>
                    <p className="text-[12.5px]" style={{ color: '#A7A3C4' }}>{listing.sellerTower} · Unit {listing.sellerUnit}</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-2.5">
                {listing.status === 'Available' && (
                  <button className="btn-primary w-full py-3 text-[14px]">
                    {isWanted ? 'I have this — contact seller' : 'Contact seller'}
                  </button>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => setShareOpen(true)}
                    className="flex-1 flex items-center justify-center gap-2 btn-secondary py-2.5 text-[13px]"
                  >
                    Share listing
                  </button>
                  <button
                    onClick={() => setReportOpen(true)}
                    className="flex-1 flex items-center justify-center gap-2 btn-secondary py-2.5 text-[13px]"
                    style={{ color: 'rgba(167,163,196,0.60)' }}
                  >
                    Report
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Related listings */}
          {relatedListings.length > 0 && (
            <div className="mt-12">
              <h2 className="font-display font-semibold text-[18px] mb-5" style={{ color: '#F2F4FF' }}>
                More in {listing.category}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedListings.map((rel) => (
                  <Link key={rel.id} href={`/marketplace/${rel.slug}`} className="block group focus-visible:outline-none">
                    <div className="neon-card p-4 group-focus-visible:border-[rgba(76,201,255,0.35)]">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant={getStatusBadge(rel.status)} label={rel.status} />
                      </div>
                      <h4 className="font-semibold text-[13.5px] mb-1 truncate" style={{ color: '#F2F4FF' }}>{rel.title}</h4>
                      <p className="font-display font-bold text-[16px]" style={{ color: '#F2F4FF' }}>{rel.priceDisplay}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Back CTA */}
          <div className="mt-10 pt-8" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <Link href="/marketplace" className="btn-secondary text-[14px] px-5 py-2.5 inline-flex items-center gap-2">
              <ArrowLeft size={14} /> Back to Marketplace
            </Link>
          </div>
        </div>

        <ReportModal isOpen={reportOpen} onClose={() => setReportOpen(false)} contentType="listing" contentLabel={listing.title} />
        <ShareModal isOpen={shareOpen} onClose={() => setShareOpen(false)} contentType="listing" title={listing.title} />
      </main>
      <SiteFooter />
    </div>
  );
}
